import React = require ("react");
import {VeBoxLoginCard} from "../components/card/VeBoxLoginCard";
import {Display} from "../components/Display";
import {VeboxLoginInput} from "../components/card/VeboxLoginInput/VeboxLoginInput";
import {Loader} from "../components/Utils/loader/Loader";
import {Button, Checkbox} from "@vebox/etui-core";
import {RestDataLoader} from "../dashboard/widgets/unilever/RestDataLoader";
import {VeboxModels} from "../types/veboxModels";
import {veboxDefaultTheme} from "../index";

const MANAGE_COMPANY_INFO = "MANAGE_COMPANY_INFO"

type country = {
    name: string,
    displayName: string
}

export interface CompleteProfilePageTexts {
    completeProfileTitle;
    name;
    surname;
    country;
    company;
    submit;
    receiveEmailNotifications;
    receivePushNotifications;
    userUpdatedSuccessfully;
    alarmSettingsTitle;
    alarmSettingsDescription
    alarmSettingsHowToChange
}

export interface RequestParam {
    op: "add" | "replace"
    path: requestParamPath
    value: string
}

interface CompleteProfilePageProps {
    texts: CompleteProfilePageTexts;
    updateProfile: (param: RequestParam[]) => void
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
    userUpdatedSuccessfully: boolean;
    errorMessage: string;
    currentUser: VeboxModels.User
}

type valuesToBeUpdated = {
    name: string,
    surname: string,
    companyName: string,
    companyCountry: string,
    receiveEmailNotifications: boolean,
    receivePushNotifications: boolean
}

const enum valueType {
    name = "name",
    surname = "surname",
    companyName = "companyName",
    companyCountry = "companyCountry",
    receiveEmailNotifications = "receiveEmailNotifications",
    receivePushNotifications = "receivePushNotifications",
}

const enum requestParamPath {
    companyName = "/company/name",
    companyCountry = "/company/country",
    receiveEmailNotifications = "/receiveEmailNotifications",
    receivePushNotifications = "/receivePushNotifications",
    name = "/name",
    surname = "/surname"
}

const paramPath = {
    companyName: requestParamPath.companyName,
    companyCountry: requestParamPath.companyCountry,
    receiveEmailNotifications: requestParamPath.receiveEmailNotifications,
    receivePushNotifications: requestParamPath.receivePushNotifications,
    name: requestParamPath.name,
    surname: requestParamPath.surname
}

const mapDataForRequest = (values: valuesToBeUpdated, countryOptions: country[]): RequestParam[] => {
    const list: RequestParam[] = [];
    Object.keys(values).forEach((key) => {
        list.push({
            op: "replace",
            path: paramPath[key],
            value: paramPath[key] === requestParamPath.companyCountry
                ? countryOptions.find((c) => c.displayName === values[key])?.name
                : values[key]
        })
    })
    return list
}

export const CompleteProfilePage: React.FC<CompleteProfilePageProps> = ({
                                                                            texts,
                                                                            updateProfile,
                                                                            isLoading,
                                                                            setIsLoading,
                                                                            userUpdatedSuccessfully,
                                                                            errorMessage,
                                                                            currentUser
                                                                        }) => {

    const [values, setValues] = React.useState<valuesToBeUpdated>();
    const [isSubmittable, setIsSubmittable] = React.useState<boolean>(false);
    const [countryOptions, setCountryOptions] = React.useState<country[]>([]);
    const [canEditCompany, setCanEditCompany] = React.useState<boolean>(false);

    const updateTextInput = (name: valueType) => (e) => {
        updateValue(name, e.target.value)
    }
    const updateBooleanInput = (name: valueType) => (e) => updateValue(name, e.target.checked)
    const updateValue = (name: valueType, value: any) => {
        setValues(prevValues => ({
            ...prevValues,
            [name]: value
        }))
    }
    const completeProfile = () => {
        setIsLoading(true);
        updateProfile(mapDataForRequest(values, countryOptions))
    }

    const validateSubmittable = () => {
        return (Boolean(values)
            && (values?.name !== undefined && values?.name !== "")
            && (values?.surname !== undefined && values?.surname !== "")
            && (!canEditCompany
                || ((values?.companyName !== undefined && values?.companyName !== "") && (values?.companyCountry !== undefined && values?.companyCountry !== ""))));
    }

    React.useEffect(() => {
        setIsSubmittable(validateSubmittable());
    }, [values])

    React.useEffect(() => {
        if (currentUser) {
            const canEdit = Boolean(
                currentUser.rolesAndPermissions.find(
                    (r) => Boolean(r.permissions.find((p) => p.code === MANAGE_COMPANY_INFO))))
            setCanEditCompany(canEdit)
            if (canEdit) {
                const newValues = values;
                delete newValues?.companyCountry;
                delete newValues?.companyName;
                setValues(newValues);
            }
        }
    }, [currentUser])

    React.useEffect(() => {
        getCountries()
            .then((countriesList: country[]) => {
                const orderedCountries = countriesList.sort((c1, c2) => (c1.displayName > c2.displayName ? 1 : -1));
                setCountryOptions(orderedCountries)
                if (canEditCompany) {
                    updateValue(valueType.companyCountry, orderedCountries[0].displayName)
                }
            })
    }, [])

    return (
        <VeBoxLoginCard className={"first-login-card"}>
            <VeBoxLoginCard.Header className={"first-login-card-header"}>
                <VeBoxLoginCard.Title text={texts?.completeProfileTitle}/>
            </VeBoxLoginCard.Header>
            <VeBoxLoginCard.Body className={"first-login-card-body"}>
                <Display isVisible={isLoading}>
                    <div className={"loader-section"}>
                        <Loader color={veboxDefaultTheme.palette.brand.main}/>
                    </div>
                </Display>
                <Display isVisible={!isLoading}>
                    <Display isVisible={errorMessage && userUpdatedSuccessfully}>
                        <p className={"errorInfo"}>{errorMessage}</p>
                    </Display>
                    <Display isVisible={userUpdatedSuccessfully}>
                        <span className={"first-login-intro-text"}>
                            {texts?.userUpdatedSuccessfully}
                        </span>
                    </Display>
                    <Display isVisible={!userUpdatedSuccessfully}>
                        <VeboxLoginInput
                            type={"text"}
                            placeholder={texts?.name}
                            value={values?.name ?? ""}
                            onChange={updateTextInput(valueType.name)}
                        />
                        <VeboxLoginInput
                            type={"text"}
                            value={values?.surname ?? ""}
                            placeholder={texts?.surname}
                            onChange={updateTextInput(valueType.surname)}
                        />
                        <Display isVisible={canEditCompany}>
                            <VeboxLoginInput
                                type={"text"}
                                value={values?.companyName ?? ""}
                                placeholder={texts?.company}
                                onChange={updateTextInput(valueType.companyName)}
                            />
                            <select
                                className={"loginInputField"}
                                value={values?.companyCountry ?? ""}
                                onChange={updateTextInput(valueType.companyCountry)}
                                placeholder={texts?.country}
                            >
                                {countryOptions.map((opt, i) => <option key={i}>{opt.displayName}</option>)}
                            </select>
                        </Display>
                        <p className={"first-login-intro-title pb-7 pt-7"}>{texts?.alarmSettingsTitle}</p>
                        <p className={"first-login-intro-text pb-7 pt-7"}>
                            {texts?.alarmSettingsDescription}
                        </p>
                        <p className={"first-login-intro-text pb-7 pt-7"}>
                            {texts?.alarmSettingsHowToChange}
                        </p>
                        <div className={"notification-text"}>
                            <Checkbox
                                onChange={updateBooleanInput(valueType.receiveEmailNotifications)}
                            />
                            <span> {texts?.receiveEmailNotifications}</span>
                        </div>
                        <div className={"notification-text"}>
                            <Checkbox
                                className={"notification-checkbox"}
                                onChange={updateBooleanInput(valueType.receivePushNotifications)}
                            />
                            <span> {texts?.receivePushNotifications}</span>
                        </div>
                    </Display>
                </Display>
            </VeBoxLoginCard.Body>
            <VeBoxLoginCard.Footer className={"first-login-card-footer"}>
                <Display isVisible={!isLoading && !userUpdatedSuccessfully}>
                    <Button
                        disabled={!isSubmittable}
                        variant={"strong"}
                        onClick={completeProfile}
                    >
                        {texts?.submit}
                    </Button>
                </Display>
            </VeBoxLoginCard.Footer>
        </VeBoxLoginCard>
    )
}


const getCountries = (): Promise<country[]> => {
    return new Promise<country[]>((resolve, reject) => {
        RestDataLoader.loadData(
            `support/countries`,
            {},
            (result: { list: country[] }) => resolve(result.list),
            reject,
            false
        )
    })
}
